import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateFieldCropPractice, updateFieldCropPracticeAdmin } from "services";

import { getEnvironment, invalidateCarbonProgramQueriesByChunk, invalidateQueriesByChunk } from "utils";

import {
  GET_ADMIN_FARM_ORGANIC_FERTILIZER_PRODUCTION,
  GET_FARM_DATA_SUBMISSION_STATUS,
  GET_FARM_ORGANIC_FERTILIZER_PRODUCTION,
  GET_FARM_REPORTING_YEAR,
  GET_FARM_RESILIENCE_SCORE,
  GET_FIELDS_CROP_RECOMMENDATIONS,
  GET_FIELD_PRACTICES,
  GET_FIELD_PRACTICE_FILTER_OPTIONS,
  GET_FIELD_PRACTICE_TIMESTAMPS,
  GET_FIELD_TASKS,
  GET_INCENTIVES_POTENTIAL_PAYOUTS,
  GET_VERIFICATION_EDITING_REQUESTS,
  UPDATE_FIELD_CROP_PRACTICE,
} from "consts";

import { CropPracticesRequestBody } from "models";

type MutateParams = {
  fieldId: string;
  cropId: string;
  requestBody: CropPracticesRequestBody;
};

export const useUpdateFieldCropPractice = ({ fieldId, farmId }: { fieldId: string; farmId?: string }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_FIELD_CROP_PRACTICE, fieldId],
    mutationFn: (data: Pick<MutateParams, "cropId" | "requestBody">) => mutate({ fieldId, ...data }),
    onSuccess: () => {
      invalidateCarbonProgramQueriesByChunk(fieldId);
      queryClient.invalidateQueries({ queryKey: [GET_INCENTIVES_POTENTIAL_PAYOUTS] });
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICE_FILTER_OPTIONS] });

      invalidateQueriesByChunk(GET_FIELD_PRACTICES, fieldId);
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_PRACTICE_TIMESTAMPS, fieldId] });
      queryClient.invalidateQueries({ queryKey: [GET_FIELDS_CROP_RECOMMENDATIONS, [fieldId]] });
      queryClient.invalidateQueries({ queryKey: [GET_VERIFICATION_EDITING_REQUESTS, farmId] });
      queryClient.invalidateQueries({ queryKey: [GET_FARM_REPORTING_YEAR, farmId] });
      // TODO: Only invalidate query for specific field, once chunking is implemented.
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_TASKS] });
      queryClient.invalidateQueries({ queryKey: [GET_FARM_DATA_SUBMISSION_STATUS, farmId] });
      if (farmId) {
        queryClient.invalidateQueries({ queryKey: [GET_FARM_RESILIENCE_SCORE, farmId] });
        queryClient.invalidateQueries({ queryKey: [GET_FARM_ORGANIC_FERTILIZER_PRODUCTION, farmId] });
        queryClient.invalidateQueries({ queryKey: [GET_ADMIN_FARM_ORGANIC_FERTILIZER_PRODUCTION, farmId] });
      }
    },
  });
};

const mutate = ({ fieldId, cropId, requestBody }: MutateParams) => {
  const isAdmin = getEnvironment().admin;

  if (isAdmin) {
    return updateFieldCropPracticeAdmin({ fieldId, cropId, requestBody });
  }

  return updateFieldCropPractice({ fieldId, cropId, requestBody });
};
